/* eslint-disable max-lines -- accounting settings keeps connection, mapping, and audit panels together */
import * as React from 'react'
import { Building2 } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { deleteJSON, getJSON, patchJSON, postJSON } from '@/lib/api'
import { accountingErrorMessage } from '@/pages/accountingErrors'
import { useViewOnly } from '@/components/templates/app-shell'
import { AccountingActivity } from '@/pages/AccountingActivity'
import {
  AccountingMappingSelect,
  type AccountingProviderReference,
} from '@/pages/AccountingMappingSelect'
import {
  AccountingConnectionAudit,
  AccountingConnectionNotice,
  ConnectedCompany,
  DisconnectedCompany,
} from '@/pages/AccountingConnectionPanel'
import {
  PaymentDepositAccountMappings,
  type PaymentDepositAccountMappingsResponse,
} from '@/pages/PaymentDepositAccountMappings'
import {
  PayoutAccountMappings,
  type PayoutAccountMappingsResponse,
} from '@/pages/PayoutAccountMappings'
import type { RevenueDestinationMappingsResponse } from '@/pages/revenueDestinationTypes'

export { accountingErrorMessage } from '@/pages/accountingErrors'

export interface AccountingSettingsResponse {
  accounting_settings: AccountingSettings
}

export interface AccountingSettings {
  connection: AccountingConnection | null
  sync_health?: AccountingSyncHealth
  company_state_audit?: Array<{
    company_name: string
    revenue_destination_mappings: number
    payment_deposit_account_mappings?: number
    external_identities: number
    archived_at: string
  }>
  financial_history?: Array<{ id: number; event: string; actor: string; occurred_at: string }>
}

export interface AccountingSyncHealth {
  last_successful_push_at: string | null
  queue_depth: number
  open_task_counts: { needs_mapping: number; drift: number; error: number }
}

export interface AccountingConnection {
  id: number
  provider: string
  company_name: string
  connected_by: string
  connected_at: string
  status: 'connected' | 'disconnected'
  disconnected_reason: string | null
}

interface AuthorizationResponse {
  accounting_connection_authorization: { authorization_url: string; state: string }
}

type RevenueDestination = AccountingProviderReference

export function AccountingSettingsPage() {
  const viewOnly = useViewOnly()
  const queryClient = useQueryClient()
  const callback = callbackParameters()
  const callbackStarted = React.useRef(false)
  const [callbackFinished, setCallbackFinished] = React.useState(false)
  const settingsQuery = useQuery({
    queryKey: ['accounting-settings'],
    queryFn: () => getJSON<AccountingSettingsResponse>('/api/v1/accounting_settings'),
    retry: false,
  })
  const startAuthorization = useMutation<AuthorizationResponse, Error, boolean>({
    mutationFn: (confirmCompanyChange) => {
      if (!confirmCompanyChange) sessionStorage.removeItem('accounting-company-change-confirmed')
      return postJSON<AuthorizationResponse>('/api/v1/accounting_connection_authorizations', {})
    },
    onSuccess: ({ accounting_connection_authorization: authorization }, confirmCompanyChange) => {
      if (confirmCompanyChange)
        sessionStorage.setItem('accounting-company-change-confirmed', 'true')
      window.location.assign(authorization.authorization_url)
    },
  })
  const completeAuthorization = useMutation({
    mutationFn: (parameters: NonNullable<ReturnType<typeof callbackParameters>>) =>
      postJSON<AccountingSettingsResponse>('/api/v1/accounting_connections', {
        accounting_connection: parameters,
      }),
    onSuccess: (settings) => {
      sessionStorage.removeItem('accounting-company-change-confirmed')
      window.history.replaceState({}, '', '/settings/accounting')
      queryClient.setQueryData(['accounting-settings'], settings)
      setCallbackFinished(true)
    },
  })

  React.useEffect(() => {
    if (!callback || callbackStarted.current) return

    callbackStarted.current = true
    completeAuthorization.mutate(callback)
  }, [callback, completeAuthorization])

  const connection = settingsQuery.data?.accounting_settings.connection
  const accountingSettings = settingsQuery.data?.accounting_settings
  const mappingsQuery = useQuery({
    queryKey: ['revenue-destination-mappings'],
    queryFn: () =>
      getJSON<RevenueDestinationMappingsResponse>('/api/v1/revenue_destination_mappings'),
    enabled: connection?.status === 'connected',
    retry: false,
  })
  const depositMappingsQuery = useQuery({
    queryKey: ['payment-deposit-account-mappings'],
    queryFn: () =>
      getJSON<PaymentDepositAccountMappingsResponse>('/api/v1/payment_deposit_account_mappings'),
    enabled: connection?.status === 'connected',
    retry: false,
  })
  const payoutMappingsQuery = useQuery({
    queryKey: ['payout-account-mappings'],
    queryFn: () => getJSON<PayoutAccountMappingsResponse>('/api/v1/payout_account_mappings'),
    enabled: connection?.status === 'connected',
    retry: false,
  })
  const error = startAuthorization.error ?? completeAuthorization.error
  const disconnect = useMutation({
    mutationFn: (id: number) => deleteJSON(`/api/v1/accounting_connections/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounting-settings'] }),
  })
  const loading =
    settingsQuery.isLoading ||
    Boolean(callback && !callbackFinished && !completeAuthorization.isError)

  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <header className="pb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-heading-sm font-semibold text-foreground">Accounting settings</h1>
          {viewOnly && <Badge variant="info">View only</Badge>}
        </div>
      </header>

      {(error || disconnect.error) && (
        <Alert className="mb-4 max-w-2xl">
          {accountingErrorMessage(error ?? disconnect.error!)}
        </Alert>
      )}
      <AccountingConnectionNotice connection={connection} />
      <AccountingSettingsContent
        viewOnly={viewOnly}
        loading={loading}
        failed={settingsQuery.isError}
        connection={connection}
        settings={accountingSettings}
        mappingsQuery={mappingsQuery}
        depositMappingsQuery={depositMappingsQuery}
        payoutMappingsQuery={payoutMappingsQuery}
        authorizationPending={startAuthorization.isPending}
        disconnecting={disconnect.isPending}
        onAuthorize={(confirmed) => startAuthorization.mutate(confirmed)}
        onDisconnect={(id) => disconnect.mutate(id)}
      />
    </div>
  )
}

// The connection and mapping sections intentionally share one compact card.
function AccountingSettingsContent({
  viewOnly,
  loading,
  failed,
  connection,
  settings,
  mappingsQuery,
  depositMappingsQuery,
  payoutMappingsQuery,
  authorizationPending,
  disconnecting,
  onAuthorize,
  onDisconnect,
}: {
  viewOnly: boolean
  loading: boolean
  failed: boolean
  connection: AccountingConnection | null | undefined
  settings: AccountingSettings | undefined
  mappingsQuery: ReturnType<typeof useQuery<RevenueDestinationMappingsResponse>>
  depositMappingsQuery: ReturnType<typeof useQuery<PaymentDepositAccountMappingsResponse>>
  payoutMappingsQuery: ReturnType<typeof useQuery<PayoutAccountMappingsResponse>>
  authorizationPending: boolean
  disconnecting: boolean
  onAuthorize: (confirmed: boolean) => void
  onDisconnect: (id: number) => void
}) {
  if (loading)
    return (
      <p className="text-body-sm text-muted-foreground" role="status">
        Loading accounting settings...
      </p>
    )
  if (failed) return <Alert>Accounting settings unavailable</Alert>

  return (
    <Card className="overflow-hidden">
      <CardContent className="divide-y divide-border p-0">
        <section className="p-4">
          <h2 className="flex items-center gap-2 text-body font-semibold">
            <Building2 className="h-4 w-4 text-primary" strokeWidth={1.5} />
            QuickBooks Online
          </h2>
          <div className="pt-3">
            {connection ? (
              <ConnectedCompany
                connection={connection}
                health={settings?.sync_health}
                pending={authorizationPending}
                onReconnect={() => onAuthorize(false)}
                onChangeCompany={() => onAuthorize(true)}
                disconnecting={disconnecting}
                onDisconnect={() => onDisconnect(connection.id)}
                viewOnly={viewOnly}
              />
            ) : viewOnly ? null : (
              <DisconnectedCompany
                pending={authorizationPending}
                onConnect={() => onAuthorize(false)}
              />
            )}
          </div>
        </section>
        {connection?.status === 'connected' && (
          <RevenueDestinationMappings query={mappingsQuery} readOnly={viewOnly} />
        )}
        {connection?.status === 'connected' && (
          <PaymentDepositAccountMappings query={depositMappingsQuery} readOnly={viewOnly} />
        )}
        {connection?.status === 'connected' && (
          <PayoutAccountMappings query={payoutMappingsQuery} readOnly={viewOnly} />
        )}
        {connection && <AccountingActivity />}
        {settings && <AccountingConnectionAudit settings={settings} />}
      </CardContent>
    </Card>
  )
}

function RevenueDestinationMappings({
  query,
  readOnly,
}: {
  query: ReturnType<typeof useQuery<RevenueDestinationMappingsResponse>>
  readOnly: boolean
}) {
  const queryClient = useQueryClient()
  const update = useMutation({
    mutationFn: (values: Record<string, number | string | null>) =>
      patchJSON<RevenueDestinationMappingsResponse>('/api/v1/revenue_destination_mappings', {
        revenue_destination_mappings: values,
      }),
    onSuccess: (data) => queryClient.setQueryData(['revenue-destination-mappings'], data),
  })

  if (query.isLoading) {
    return <p className="text-body-sm text-muted-foreground">Loading Revenue Destinations...</p>
  }
  if (query.isError || !query.data) return <Alert>Revenue Destinations unavailable</Alert>

  const mappings = query.data.revenue_destination_mappings
  return (
    <section className="p-4">
      <h2 className="text-body font-semibold">Revenue Destinations</h2>
      <div className="space-y-4 pt-3">
        {update.error && <Alert>{accountingErrorMessage(update.error)}</Alert>}
        <RevenueDestinationMappingField
          id="manual-line-default"
          label="Manual-line default"
          needsMapping={mappings.manual_line_default_needs_mapping}
          destinations={mappings.destinations}
          value={mappings.manual_line_default?.id}
          disabled={update.isPending}
          readOnly={readOnly}
          onChange={(destinationId) =>
            update.mutate({ manual_line_default_destination_id: destinationId })
          }
        />
        <RevenueDestinationMappingField
          id="discount-destination"
          label="Discount destination"
          needsMapping={mappings.discount_destination_needs_mapping}
          destinations={mappings.destinations}
          value={mappings.discount_destination?.id}
          disabled={update.isPending}
          readOnly={readOnly}
          onChange={(destinationId) => update.mutate({ discount_destination_id: destinationId })}
        />
        <RevenueDestinationMappingField
          id="donation-default"
          label="Donations default"
          needsMapping={mappings.donation_default_needs_mapping}
          destinations={mappings.destinations}
          value={mappings.donation_default?.id}
          disabled={update.isPending}
          readOnly={readOnly}
          onChange={(destinationId) =>
            update.mutate({ donation_default_destination_id: destinationId })
          }
        />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Membership Level</TableHead>
              <TableHead>Revenue Destination</TableHead>
              <TableHead className="w-28">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mappings.membership_levels.map((level) => (
              <TableRow key={level.id}>
                <TableCell className="font-medium">{level.name}</TableCell>
                <TableCell>
                  <AccountingMappingSelect
                    destinations={mappings.destinations}
                    value={level.destination?.id}
                    disabled={update.isPending}
                    readOnly={readOnly}
                    onChange={(destinationId) =>
                      update.mutate({
                        membership_level_id: level.id,
                        destination_id: destinationId,
                      })
                    }
                  />
                </TableCell>
                <TableCell>
                  {level.needs_mapping || !level.destination ? (
                    <Badge variant="warning">Needs mapping</Badge>
                  ) : (
                    <Badge variant="success">Mapped</Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {mappings.tasks.length > 0 && (
          <section className="space-y-2" aria-labelledby="accounting-tasks-title">
            <h3 id="accounting-tasks-title" className="text-body-sm font-semibold">
              Accounting tasks
            </h3>
            {mappings.tasks.map((task) => (
              <div
                key={task.name}
                className="flex flex-wrap items-center gap-2 border-t border-border py-2 text-body-sm"
              >
                <div>
                  <p className="font-medium text-foreground">{task.name}</p>
                  <p className="text-caption text-muted-foreground">{task.source_name}</p>
                </div>
                <Badge variant="warning">Needs mapping</Badge>
              </div>
            ))}
          </section>
        )}
      </div>
    </section>
  )
}

function RevenueDestinationMappingField({
  id,
  label,
  needsMapping,
  destinations,
  value,
  disabled,
  readOnly,
  onChange,
}: {
  id: string
  label: string
  needsMapping: boolean
  destinations: RevenueDestination[]
  value?: string
  disabled: boolean
  readOnly: boolean
  onChange: (destinationId: string | null) => void
}) {
  return (
    <div className="max-w-md space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <Label htmlFor={id}>{label}</Label>
        {needsMapping && <Badge variant="warning">Needs mapping</Badge>}
      </div>
      {readOnly ? (
        <p className="text-body-sm text-muted-foreground">
          {destinations.find((item) => item.id === value)?.name ?? 'Unmapped'}
        </p>
      ) : (
        <AccountingMappingSelect
          id={id}
          destinations={destinations}
          value={value}
          disabled={disabled}
          onChange={onChange}
        />
      )}
    </div>
  )
}

function callbackParameters() {
  const parameters = new URLSearchParams(window.location.search)
  const code = parameters.get('code')
  const state = parameters.get('state')
  const companyId = parameters.get('realmId')
  if (!code || !state || !companyId) return null

  const confirmCompanyChange =
    sessionStorage.getItem('accounting-company-change-confirmed') === 'true'
  sessionStorage.removeItem('accounting-company-change-confirmed')
  return {
    code,
    state,
    company_id: companyId,
    ...(confirmCompanyChange ? { confirm_company_change: true } : {}),
  }
}
